import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost } from "../../lib";
import { Navigation } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 1200, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(2) },
  chip: { fontWeight: 600 },
  viewBtn: { textTransform: "none" },
}));

const STATUS_COLORS = {
  success:         { label: "Success",         style: { backgroundColor: "#e8f5e9", color: "#2e7d32" } },
  partial_failure: { label: "Partial Failure",  style: { backgroundColor: "#fff3e0", color: "#e65100" } },
  failed:          { label: "Failed",           style: { backgroundColor: "#ffebee", color: "#c62828" } },
  running:         { label: "Running",          style: { backgroundColor: "#e3f2fd", color: "#1565c0" } },
};

function formatDay(dateStr) {
  // dateStr is YYYY-MM-DD; use noon to avoid timezone shifts
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short", year: "numeric", month: "short", day: "numeric",
  });
}

function groupByDay(runs) {
  const map = {};
  for (const run of runs) {
    const date = new Date(run.started_at).toLocaleDateString("en-CA"); // YYYY-MM-DD
    if (!map[date]) map[date] = [];
    map[date].push(run);
  }
  return Object.keys(map)
    .sort()
    .reverse()
    .map((date) => {
      const dayRuns      = map[date];
      const totalCreated  = dayRuns.reduce((s, r) => s + (r.items_created  || 0), 0);
      const totalUpdated  = dayRuns.reduce((s, r) => s + (r.items_updated  || 0), 0);
      const totalSucceeded = dayRuns.reduce((s, r) => s + (r.items_succeeded || 0), 0);
      const totalAttempted = dayRuns.reduce((s, r) => s + (r.items_attempted || 0), 0);
      const hasRunning = dayRuns.some((r) => r.status === "running");
      const hasFailure = dayRuns.some((r) => r.status === "failed" || r.status === "partial_failure");
      const allFailed  = dayRuns.every((r) => r.status === "failed");
      const status = hasRunning ? "running" : hasFailure ? (allFailed ? "failed" : "partial_failure") : "success";
      return { date, totalCreated, totalUpdated, totalSucceeded, totalAttempted, status, runCount: dayRuns.length };
    });
}

export function MagentoSyncPage() {
  const classes = useStyles();
  const [runs, setRuns]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [snack, setSnack]         = useState({ open: false, msg: "" });

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    const { data, error } = await authGet("/magento-sync/runs");
    setLoading(false);
    if (error) { showSnack(error?.message || "Failed to load sync runs"); return; }
    setRuns(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  function showSnack(msg) { setSnack({ open: true, msg }); }

  async function handleTrigger() {
    setTriggering(true);
    const { data, error } = await authPost("/magento-sync/trigger");
    setTriggering(false);
    if (error) { showSnack(error?.message || "Sync trigger failed"); return; }
    showSnack(data?.message || `Run ${data?.id} completed`);
    fetchRuns();
  }

  const days = groupByDay(runs);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h6">Magento Sync Log</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTrigger}
          disabled={triggering}
        >
          {triggering ? "Triggering…" : "Trigger Sync Now"}
        </Button>
      </Box>

      {loading ? (
        <CircularProgress size={28} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="center">Created</TableCell>
                <TableCell align="center">Updated</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map((day) => {
                const sc = STATUS_COLORS[day.status] || STATUS_COLORS.running;
                return (
                  <TableRow key={day.date} hover>
                    <TableCell>{formatDay(day.date)}</TableCell>
                    <TableCell align="center">{day.totalCreated || "—"}</TableCell>
                    <TableCell align="center">{day.totalUpdated || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${sc.label} (${day.totalSucceeded}/${day.totalAttempted})`}
                        className={classes.chip}
                        style={sc.style}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.viewBtn}
                        onClick={() => Navigation.go(`/magento-sync/day/${day.date}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {days.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ color: "#999", padding: 24 }}>
                    No sync runs yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}